import { useMemo, useCallback, useSyncExternalStore, useState } from 'react';
import { clipTranslationLoader, onLanguageChange, requestCaptionTranslation } from '@/stores/clip';
import { LANGUAGE_OPTIONS, LanguageOption } from '@/Pages/Clip/CaptionTranslation/types';
import { Clip } from '@/domains/asset';
import { isClipInOriginalLanguage } from '@/libs/clipUtils';
import { useAppContext } from '@/context/AppContext/AppContext';
import useAnalytics from '@/hooks/useAnalytics';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';

export default function useTranslationState(clipData: Clip) {
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageOption | null>(null);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);

  const { adminAppStore } = useAppContext();
  const { trackCaptionTranslationRestricted } = useAnalytics();

  const allowCaptionTranslation = adminAppStore.getters?.['orgPricingConfig/getAllowCaptionTranslation'];

  const clipCaptionsLoaderState = useSyncExternalStore(
    clipTranslationLoader.subscribe,
    clipTranslationLoader.getSnapshot
  );
  const clipCaptionsLoading = clipCaptionsLoaderState[clipData.id] || false;

  const isTranslationProcessing = useMemo(() => {
    return clipData?.translation_status === 'PROCESSING' || clipCaptionsLoading;
  }, [clipData?.translation_status, clipCaptionsLoading]);

  const hasMultipleTranslatedLanguages = useMemo(() => {
    return clipData.asset_metadata?.translated_langs?.length > 1;
  }, [clipData.asset_metadata]);

  const onRegenerateTranslation = useCallback(
    (language: LanguageOption) => {
      requestCaptionTranslation(clipData.id, language);
    },
    [clipData.id]
  );

  const translatedLanguagesOptionsWithoutAction = useMemo(() => {
    const translatedLanguages = clipData.asset_metadata?.translated_langs || [];
    const originalLanguage = clipData.asset_metadata?.original_lang || 'en';

    return translatedLanguages.map(lang => ({
      name: lang.code === originalLanguage ? 'Original' : lang.name,
      code: lang.code,
      subText: lang.code === originalLanguage ? `(${lang.name})` : ''
    }));
  }, [clipData.asset_metadata]);

  const translatedLanguagesOptions = useMemo(() => {
    const translatedLanguages = clipData.asset_metadata?.translated_langs || [];
    const originalLanguage = clipData.asset_metadata?.original_lang || 'en';

    return translatedLanguages.map(lang => ({
      name: lang.code === originalLanguage ? 'Original' : lang.name,
      code: lang.code,
      subText: lang.code === originalLanguage ? `(${lang.name})` : '',
      ...(lang.code !== originalLanguage && {
        action: {
          label: 'Regenerate translation',
          isDisabled: isTranslationProcessing,
          onClick: () => {
            onRegenerateTranslation(lang);
          },
          icon: 'IconRefreshDot'
        }
      })
    }));
  }, [clipData.asset_metadata, isTranslationProcessing, onRegenerateTranslation]);

  const selectedLanguageName = useMemo(() => {
    let code = clipData.asset_metadata?.selected_lang;
    if (!code && translatedLanguagesOptions.length === 1) {
      code = translatedLanguagesOptions[0].code;
    }
    const language = translatedLanguagesOptions.find(lang => lang.code === code);
    if (code === clipData.asset_metadata?.original_lang) {
      return 'Original';
    }
    return language?.name ?? 'English';
  }, [clipData.asset_metadata, translatedLanguagesOptions]);

  const isOriginalLanguageSelected = useMemo(() => {
    return isClipInOriginalLanguage(clipData);
  }, [clipData]);

  const isOriginalLanguageSupportedForTranslation = useMemo(() => {
    const originalLangCode = clipData.asset_metadata?.original_lang;
    if (!originalLangCode) {
      return true;
    }

    return LANGUAGE_OPTIONS.some(lang => lang.code === originalLangCode);
  }, [clipData.asset_metadata?.original_lang]);

  const closeWarningModal = useCallback(() => {
    setIsWarningModalOpen(false);
  }, []);

  const checkLanguageAndExecute = useCallback(
    (action: () => void) => {
      if (isOriginalLanguageSupportedForTranslation) {
        action();
      } else {
        setIsWarningModalOpen(true);
      }
    },
    [isOriginalLanguageSupportedForTranslation]
  );

  const onLanguageSelection = useCallback(
    (languageName: string) => {
      const language = translatedLanguagesOptions.find(lang => lang.name === languageName);
      const isAlreadySelected = clipData.asset_metadata?.selected_lang === language?.code;
      if (language && !isAlreadySelected) {
        onLanguageChange(clipData.id, language);
      }
    },
    [clipData.id, translatedLanguagesOptions, clipData.asset_metadata?.selected_lang]
  );

  const onCaptionTranslationConfirm = useCallback(
    (language: LanguageOption) => {
      if (!language) {
        return;
      }
      requestCaptionTranslation(clipData.id, language);
    },
    [clipData.id]
  );

  const availableLanguagesForTranslation = useMemo(() => {
    const availableLanguages = LANGUAGE_OPTIONS.filter(
      lang =>
        lang.code !== clipData.asset_metadata?.original_lang &&
        !clipData.asset_metadata?.translated_langs?.some(translated_lang => translated_lang.code === lang.code)
    );
    return availableLanguages.map(lang => ({
      name: lang.name,
      code: lang.code
    }));
  }, [clipData.asset_metadata?.original_lang, clipData.asset_metadata?.translated_langs]);

  const translatedLanguages = useMemo(() => {
    const translatedLanguages = clipData.asset_metadata?.translated_langs || [];

    return translatedLanguages.filter(lang => lang.code !== clipData.asset_metadata?.original_lang);
  }, [clipData.asset_metadata?.translated_langs, clipData.asset_metadata?.original_lang]);

  const originalLanguage = useMemo(() => {
    const originalLanguage = LANGUAGE_OPTIONS.filter(lang => lang.code === clipData.asset_metadata?.original_lang);
    return originalLanguage.map(lang => ({
      name: 'Original',
      code: lang.code,
      subText: `(${lang.name})`
    }));
  }, [clipData.asset_metadata?.original_lang]);

  const findLanguage = useCallback(
    (selected: string) => {
      if (selected === 'Original') {
        return originalLanguage.find(lang => lang.name === selected);
      }
      return LANGUAGE_OPTIONS.find(lang => lang.name === selected);
    },
    [originalLanguage]
  );

  const handleLanguageChange = useCallback(
    (selected: string) => {
      const language = findLanguage(selected);
      if (!language) {
        return;
      }

      setSelectedLanguage(language);

      const isOriginalLanguage = selected === 'Original';
      const isTranslatedLanguage = translatedLanguages.some(lang => lang.name === language.name);

      if (isTranslatedLanguage || isOriginalLanguage) {
        isOriginalLanguage ? setIsDisabled(true) : setIsDisabled(false);
        EventBus.dispatch(CustomEvents.OpenEditCaptions);
        onLanguageSelection(language.name);
        return;
      }

      setIsDisabled(false);
    },
    [findLanguage, onLanguageSelection, translatedLanguages]
  );

  const handleTranslation = useCallback(
    (selectedLanguage: LanguageOption | null, onCaptionTranslation?: (language: LanguageOption | null) => void) => {
      if (selectedLanguage) {
        if (onCaptionTranslation) {
          onCaptionTranslation(selectedLanguage);
        } else {
          onCaptionTranslationConfirm(selectedLanguage);
        }
      }
    },
    [onCaptionTranslationConfirm]
  );

  const resetSelectedLanguage = useCallback(() => {
    setSelectedLanguage(null);
  }, []);

  const handleCaptionTranslationRestricted = useCallback(() => {
    trackCaptionTranslationRestricted();
  }, [trackCaptionTranslationRestricted]);

  return {
    isTranslationProcessing,
    translatedLanguagesOptions,
    translatedLanguagesOptionsWithoutAction,
    selectedLanguageName,
    onLanguageSelection,
    onCaptionTranslationConfirm,
    isOriginalLanguageSelected,
    hasMultipleTranslatedLanguages,
    availableLanguagesForTranslation,
    translatedLanguages,
    originalLanguage,
    selectedLanguage,
    isDisabled,
    findLanguage,
    handleLanguageChange,
    handleTranslation,
    resetSelectedLanguage,
    allowCaptionTranslation,
    handleCaptionTranslationRestricted,
    isOriginalLanguageSupportedForTranslation,
    checkLanguageAndExecute,
    isWarningModalOpen,
    closeWarningModal
  };
}
